import React from "react";
import { LoadingState } from "../../hooks/tenant/useTenantLoading";

const defaultPrimaryColor = "#6B7280"; // gray-500

interface LoadingProps {
  state: LoadingState;
  retryCount: number;
  error: Error | null;
  onRetry: () => void;
}

const LoadingSpinner = () => {
  const [showEmoji, setShowEmoji] = React.useState(true);

  return (
    <div className="relative w-32 h-32">
      <div
        className="absolute inset-0 animate-ping rounded-full opacity-75"
        style={{ backgroundColor: defaultPrimaryColor }}
      ></div>
      <div
        className="absolute inset-2 animate-ping delay-75 rounded-full opacity-50"
        style={{ backgroundColor: defaultPrimaryColor }}
      ></div>
      <div
        className="absolute inset-4 animate-ping delay-150 rounded-full opacity-25"
        style={{ backgroundColor: defaultPrimaryColor }}
      ></div>
      <div
        className={`absolute inset-0 flex items-center justify-center text-3xl transition-opacity duration-500 ${
          showEmoji ? "opacity-100" : "opacity-0"
        }`}
      >
        🏠
      </div>
    </div>
  );
};

const RetryMessage = ({
  count,
  onRetry,
}: {
  count: number;
  onRetry: () => void;
}) => (
  <div className="space-y-4 text-center">
    <div className="text-4xl">🏗️</div>
    <p className="text-lg font-medium text-gray-700">
      Building your experience...
    </p>
    <p className="text-sm text-gray-500">Attempt {count} of 3</p>
    <button
      onClick={onRetry}
      className="px-6 py-2 text-white rounded-full shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all"
      style={{ backgroundColor: defaultPrimaryColor }}
    >
      Try Again
    </button>
  </div>
);

const ErrorMessage = ({ error }: { error: Error }) => (
  <div className="space-y-4 text-center">
    <div className="text-4xl">🏚️</div>
    <p className="text-lg font-medium text-gray-700">
      Oops! Something went wrong
    </p>
    <p className="text-sm text-gray-500">{error.message}</p>
    <p className="text-xs text-gray-400">Try refreshing the page to rebuild</p>
  </div>
);

export const TenantLoadingStates: React.FC<LoadingProps> = ({
  state,
  retryCount,
  error,
  onRetry,
}) => {
  // Don't render anything in immediate state
  if (state === "immediate") {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-white flex items-center justify-center z-[9999]">
      <div className="space-y-8 p-8 rounded-2xl text-center">
        {state === "delayed" && (
          <div className="flex flex-col items-center space-y-4">
            <LoadingSpinner />
            <p
              className="text-xl font-medium mt-6 animate-pulse"
              style={{ color: defaultPrimaryColor }}
            >
              Building your dream home experience...
            </p>
          </div>
        )}

        {state === "retry" && (
          <RetryMessage count={retryCount} onRetry={onRetry} />
        )}

        {state === "error" && error && <ErrorMessage error={error} />}
      </div>
    </div>
  );
};
